import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    withStyles,
} from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from 'material-ui-pickers';
import LuxonUtils from '@date-io/luxon';
import { DateTime } from 'luxon';
import {
    getOwnerDashboardStartDate,
    getOwnerDashboardEndDate,
} from '../../stores/dashboard/selectors';
import { setOwnerDashboardDateRange } from '../../stores/dashboard/actions';
import DateRange from '@material-ui/icons/DateRange';
import styles from './styles';

const formatRange = (start, end) => {
    return `${DateTime.fromMillis(start).toFormat(
        'MMMM d, yyyy',
    )} - ${DateTime.fromMillis(end).toFormat('MMMM d, yyyy')}`;
};

const MIN_DATE = DateTime.local(2023, 1, 1);

const DateRangeDialog = (props) => {
    const dispatch = useDispatch();
    const startDate = useSelector(getOwnerDashboardStartDate);
    const endDate = useSelector(getOwnerDashboardEndDate);
    const [open, setOpen] = useState(false);
    const [localStart, setLocalStart] = useState(startDate);
    const [localEnd, setLocalEnd] = useState(endDate);
    const [error, setError] = useState('');

    const MAX_RANGE_DAYS = 90;
    const isRangeValid = (start, end) => {
        return (
            Math.abs(
                DateTime.fromMillis(end).diff(
                    DateTime.fromMillis(start),
                    'days',
                ).days,
            ) <= MAX_RANGE_DAYS
        );
    };

    const handleOpen = () => {
        setLocalStart(startDate);
        setLocalEnd(endDate);
        setError('');
        setOpen(true);
    };
    const handleClose = () => setOpen(false);
    const handleApply = () => {
        if (isRangeValid(localStart, localEnd)) {
            dispatch(setOwnerDashboardDateRange(localStart, localEnd));
            setOpen(false);
        } else {
            setError(
                'Start Date and End Date must be no more than 90 days apart.',
            );
        }
    };

    // Update error state on date change
    React.useEffect(() => {
        if (!isRangeValid(localStart, localEnd)) {
            setError(
                'Start Date and End Date must be no more than 90 days apart.',
            );
        } else {
            setError('');
        }
    }, [localStart, localEnd]);

    return (
        <MuiPickersUtilsProvider utils={LuxonUtils}>
            <Button
                variant="outlined"
                onClick={handleOpen}
                className={props.classes.dateRangeButtonStyle}
            >
                <span style={{ flex: 1 }}>
                    {formatRange(startDate, endDate)}
                </span>
                <DateRange style={{ color: '#FABA2D', marginLeft: 8 }} />
            </Button>
            <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
                <DialogTitle>Select Date Range</DialogTitle>
                <DialogContent
                    className={props.classes.dateRangeDialogContentStyle}
                >
                    <div className={props.classes.dateRangeInfoStyle}>
                        The maximum allowed date range is 90 days.
                    </div>
                    <Grid container direction="row" justify="space-between">
                        <Grid item xs={5}>
                            <DatePicker
                                label="Start Date"
                                value={DateTime.fromMillis(localStart)}
                                onChange={(date) =>
                                    date && setLocalStart(date.valueOf())
                                }
                                disableFuture
                                maxDate={DateTime.fromMillis(localEnd)}
                                minDate={MIN_DATE}
                                format="yyyy-LL-dd"
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <DatePicker
                                label="End Date"
                                value={DateTime.fromMillis(localEnd)}
                                onChange={(date) =>
                                    date && setLocalEnd(date.valueOf())
                                }
                                disableFuture
                                minDate={
                                    DateTime.fromMillis(localStart) < MIN_DATE
                                        ? MIN_DATE
                                        : DateTime.fromMillis(localStart)
                                }
                                format="yyyy-LL-dd"
                            />
                        </Grid>
                    </Grid>
                    {error && (
                        <div className={props.classes.dateRangeErrorStyle}>
                            {error}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleClose}
                        className={props.classes.dateRangeCancelButtonStyle}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleApply}
                        className={props.classes.dateRangeApplyButtonStyle}
                        disabled={!!error}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </MuiPickersUtilsProvider>
    );
};

export default withStyles(styles)(DateRangeDialog);
